/**
 * Typed wrapper for service-financial-services wallet endpoints
 * (port 3110 by default).
 *
 * Wired endpoints (live):
 *   - POST /v1/wallet/wallets/customers/:customerId/get-or-create
 *   - POST /v1/wallet/wallets-mine          — citizen get-or-create own wallet
 *   - GET  /v1/wallet/wallets-mine          — citizen fetch own wallet
 *   - GET  /v1/wallet/wallets-mine/transactions — citizen paginated history
 *   - POST /v1/wallet/wallets-mine/cash-ins — citizen top-up via card (Spec 12)
 *   - POST /v1/wallet/wallets-mine/transfers — citizen P2P transfer (Spec 13)
 *   - POST /v1/wallet/credits     — record a TOPUP_* / DISBURSEMENT credit (operator-scoped)
 *   - POST /v1/wallet/debits      — record a withdrawal/spend debit (operator-scoped)
 *   - POST /v1/wallet/transfers   — atomic P2P transfer between two wallets (operator-scoped)
 *   - GET  /v1/wallet/wallets/:id/transactions — paginated history
 *
 * Mirrors `apps/service-financial-services/src/modules/wallet/wallet.dto.ts`.
 */
import {
  requestJson,
  type RequestJsonOptions,
} from '../api-client';
import { getCustomerToken } from '../auth/tokenStore';
import { env } from '../env';

export type WalletStatus = 'ACTIVE' | 'SUSPENDED' | 'CLOSED';
export type KycTier = 'TIER_0' | 'TIER_1' | 'TIER_2' | 'TIER_3';

/**
 * Wire shape of `WalletResponseDto`. Phase 1c shipped a slightly
 * simplified subset (`tier`, `balance`) for the top-up page; the new
 * NIPOST Pay surface needs the full server fields, so they're declared
 * optional here so the existing fixtures keep validating.
 */
export interface WalletResponse {
  readonly id: string;
  readonly customerId: string;
  readonly status: WalletStatus;
  readonly currency: string;
  readonly createdAt: string;
  /** @deprecated — Phase 1c alias; prefer `kycTier`. */
  readonly tier?: string;
  /** @deprecated — Phase 1c alias; prefer `balanceCached`. */
  readonly balance?: string;
  readonly walletNumber?: string;
  readonly tenantId?: string;
  readonly kycTier?: KycTier;
  readonly balanceCached?: string;
  readonly dailyInflowToday?: string;
  readonly dailyInflowDate?: string;
  readonly updatedAt?: string;
}

export type CreditAction =
  | 'TOPUP_CASH'
  | 'TOPUP_CARD'
  | 'TOPUP_TRANSFER'
  | 'P2P_RECEIVE'
  | 'DISBURSEMENT';

export type DebitAction =
  | 'P2P_SEND'
  | 'BILL_PAYMENT'
  | 'AIRTIME'
  | 'MAIL_PAYMENT'
  | 'GOV_PAYMENT'
  | 'WITHDRAWAL_CASH'
  | 'WITHDRAWAL_TRANSFER';

/** @deprecated kept as an alias for the Phase 1c top-up page. */
export type TopUpAction = 'TOPUP_CASH' | 'TOPUP_CARD' | 'TOPUP_TRANSFER';

export interface RecordCreditRequest {
  readonly walletId: string;
  readonly action: CreditAction;
  readonly amount: string;
  readonly currency?: string;
  readonly counterRef?: string;
  readonly idempotencyKey?: string;
  readonly metadata?: Record<string, unknown>;
}

export interface RecordDebitRequest {
  readonly walletId: string;
  readonly action: DebitAction;
  readonly amount: string;
  readonly currency?: string;
  readonly counterRef?: string;
  readonly idempotencyKey?: string;
  readonly metadata?: Record<string, unknown>;
}

export interface RecordTransferRequest {
  readonly fromWalletId: string;
  readonly toWalletId: string;
  readonly amount: string;
  readonly currency?: string;
  readonly memo?: string;
  readonly idempotencyKey?: string;
}

export interface WalletTransactionResponse {
  readonly id: string;
  readonly walletId: string;
  readonly action: string;
  readonly amount: string;
  readonly currency: string;
  readonly createdAt: string;
  /** Phase 1c shape. */
  readonly resultingBalance?: string;
  /** Server-side full shape (`balanceAfter`). */
  readonly balanceAfter?: string;
  readonly txnNumber?: string;
  readonly side?: 'DEBIT' | 'CREDIT';
  readonly status?: 'PENDING' | 'COMPLETED' | 'FAILED' | 'REVERSED';
  readonly counterRef?: string | null;
  readonly initiatedAt?: string;
  readonly completedAt?: string | null;
}

export interface TransferResponse {
  readonly debit: WalletTransactionResponse;
  readonly credit: WalletTransactionResponse;
}

export interface ListWalletTransactionsResponse {
  readonly items: readonly WalletTransactionResponse[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

type CallOpts = Pick<RequestJsonOptions, 'fetchImpl' | 'signal'> & {
  readonly bearerToken?: string;
};

function bearerOf(opts: CallOpts): string | undefined {
  return opts.bearerToken ?? getCustomerToken() ?? undefined;
}

export async function getOrCreateWallet(
  customerId: string,
  options: CallOpts = {},
): Promise<WalletResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<WalletResponse>({
    method: 'POST',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: `/v1/wallet/wallets/customers/${encodeURIComponent(customerId)}/get-or-create`,
    body: {},
    bearerToken: bearerOf(options),
    ...rest,
  });
}

/**
 * Citizen self-service: POST /v1/wallet/wallets-mine — idempotent get-or-create.
 * Requires Wallet/create-own permission (seeded by 20260509_customer_wallet_perms.sql).
 */
export async function createOwnWallet(
  options: CallOpts = {},
): Promise<WalletResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<WalletResponse>({
    method: 'POST',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: '/v1/wallet/wallets-mine',
    body: {},
    bearerToken: bearerOf(options),
    ...rest,
  });
}

/**
 * Citizen self-service: GET /v1/wallet/wallets-mine — fetch own wallet.
 * Requires Wallet/read-self permission. Returns 404 if not yet onboarded.
 */
export async function getOwnWallet(
  options: CallOpts = {},
): Promise<WalletResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<WalletResponse>({
    method: 'GET',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: '/v1/wallet/wallets-mine',
    bearerToken: bearerOf(options),
    ...rest,
  });
}

export async function recordCredit(
  body: RecordCreditRequest,
  options: CallOpts = {},
): Promise<WalletTransactionResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<WalletTransactionResponse>({
    method: 'POST',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: '/v1/wallet/credits',
    body,
    bearerToken: bearerOf(options),
    ...rest,
  });
}

export async function recordDebit(
  body: RecordDebitRequest,
  options: CallOpts = {},
): Promise<WalletTransactionResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<WalletTransactionResponse>({
    method: 'POST',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: '/v1/wallet/debits',
    body,
    bearerToken: bearerOf(options),
    ...rest,
  });
}

export async function recordTransfer(
  body: RecordTransferRequest,
  options: CallOpts = {},
): Promise<TransferResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<TransferResponse>({
    method: 'POST',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: '/v1/wallet/transfers',
    body,
    bearerToken: bearerOf(options),
    ...rest,
  });
}

// ---------------------------------------------------------------------------
// Citizen cash-in (Spec 12) — POST /v1/wallet/wallets-mine/cash-ins
// ---------------------------------------------------------------------------

export type CashInMethod = 'CARD' | 'BANK_TRANSFER';

export interface CitizenCashInRequest {
  readonly amount: string;
  readonly currency?: string;
  readonly method?: CashInMethod;
  readonly cardToken?: string;
  readonly idempotencyKey?: string;
}

export interface CitizenCashInResponse {
  readonly transactionId: string;
  readonly txnNumber: string;
  readonly walletId: string;
  readonly walletBalance: string;
  readonly amount: string;
  readonly currency: string;
  readonly method: string;
  readonly providerReference: string | null;
}

export async function citizenCashIn(
  body: CitizenCashInRequest,
  options: CallOpts = {},
): Promise<CitizenCashInResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<CitizenCashInResponse>({
    method: 'POST',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: '/v1/wallet/wallets-mine/cash-ins',
    body,
    bearerToken: bearerOf(options),
    ...rest,
  });
}

// ---------------------------------------------------------------------------
// Citizen Paystack popup cash-in (two-step) —
//   POST /v1/wallet/wallets-mine/cash-ins/initialize → open Paystack Inline
//   POST /v1/wallet/wallets-mine/cash-ins/verify     → credit wallet
// ---------------------------------------------------------------------------

export interface CitizenCashInInitializeRequest {
  readonly amount: string;
  readonly currency?: string;
}

export interface CitizenCashInInitializeResponse {
  readonly reference: string;
  readonly accessCode: string;
  readonly publicKey: string;
  readonly amount: string;
  readonly currency: string;
}

export interface CitizenCashInVerifyRequest {
  readonly reference: string;
}

export interface CitizenCashInVerifyResponse {
  readonly transactionId: string;
  readonly txnNumber: string;
  readonly walletId: string;
  readonly walletBalance: string;
  readonly amount: string;
  readonly currency: string;
  readonly providerReference: string;
  readonly paidAt: string | null;
}

export async function citizenCashInInitialize(
  body: CitizenCashInInitializeRequest,
  options: CallOpts = {},
): Promise<CitizenCashInInitializeResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<CitizenCashInInitializeResponse>({
    method: 'POST',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: '/v1/wallet/wallets-mine/cash-ins/initialize',
    body,
    bearerToken: bearerOf(options),
    ...rest,
  });
}

export async function citizenCashInVerify(
  body: CitizenCashInVerifyRequest,
  options: CallOpts = {},
): Promise<CitizenCashInVerifyResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<CitizenCashInVerifyResponse>({
    method: 'POST',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: '/v1/wallet/wallets-mine/cash-ins/verify',
    body,
    bearerToken: bearerOf(options),
    ...rest,
  });
}

// ---------------------------------------------------------------------------
// Citizen P2P transfer (Spec 13) — POST /v1/wallet/wallets-mine/transfers
// ---------------------------------------------------------------------------

export interface CitizenTransferRequest {
  readonly recipientPhone?: string;
  readonly recipientEmail?: string;
  readonly amount: string;
  readonly currency?: string;
  readonly memo?: string;
  readonly idempotencyKey?: string;
}

export interface CitizenTransferResponse {
  readonly transferId: string;
  readonly senderBalance: string;
  readonly amount: string;
  readonly currency: string;
  readonly recipientReference: string;
}

export async function citizenTransfer(
  body: CitizenTransferRequest,
  options: CallOpts = {},
): Promise<CitizenTransferResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<CitizenTransferResponse>({
    method: 'POST',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: '/v1/wallet/wallets-mine/transfers',
    body,
    bearerToken: bearerOf(options),
    ...rest,
  });
}

export async function listWalletTransactions(
  walletId: string,
  options: CallOpts & { readonly page?: number; readonly pageSize?: number } = {},
): Promise<ListWalletTransactionsResponse> {
  const { bearerToken: _ignore, page, pageSize, ...rest } = options;
  const qs = new URLSearchParams();
  if (page !== undefined) qs.set('page', String(page));
  if (pageSize !== undefined) qs.set('pageSize', String(pageSize));
  const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : '';
  return requestJson<ListWalletTransactionsResponse>({
    method: 'GET',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: `/v1/wallet/wallets/${encodeURIComponent(walletId)}/transactions${suffix}`,
    bearerToken: bearerOf(options),
    ...rest,
  });
}

/**
 * Citizen self-service: GET /v1/wallet/wallets-mine/transactions — paginated
 * history of the citizen's own wallet transactions. Resolves the wallet from
 * the JWT (no walletId in the path). Requires Wallet/read-self. Returns an
 * empty page (total=0) when the citizen has not yet onboarded a wallet.
 */
export async function listOwnWalletTransactions(
  options: CallOpts & { readonly page?: number; readonly pageSize?: number } = {},
): Promise<ListWalletTransactionsResponse> {
  const { bearerToken: _ignore, page, pageSize, ...rest } = options;
  const qs = new URLSearchParams();
  if (page !== undefined) qs.set('page', String(page));
  if (pageSize !== undefined) qs.set('pageSize', String(pageSize));
  const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : '';
  return requestJson<ListWalletTransactionsResponse>({
    method: 'GET',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: `/v1/wallet/wallets-mine/transactions${suffix}`,
    bearerToken: bearerOf(options),
    ...rest,
  });
}
